/**
 * Paletas LOCAIS (por usuário/navegador) das escalas Cota e Diâmetro.
 * NÃO vão para o banco: sobrevivem ao reload via localStorage. Cota some ao
 * "restaurar padrão". Diâmetro é um mapa único (auto + escolha do usuário).
 * Globais (não por localId) — o 100 vermelho é o mesmo em qualquer mapa
 * deste navegador.
 *
 * { cota: string[6], diametroEscuro: { [valor]: hex }, diametroClaro: { … } }
 */

const CHAVE = 'flow.mapa-escalas.cores'
const HEX = /^#([0-9a-fA-F]{6})$/

export function ehHex(cor) {
  return typeof cor === 'string' && HEX.test(cor)
}

export function corParaHex(cor) {
  const s = String(cor || '').trim()
  const hex6 = s.match(/^#([0-9a-fA-F]{6})$/)
  if (hex6) {
    return `#${hex6[1].toLowerCase()}`
  }

  const hex3 = s.match(/^#([0-9a-fA-F]{3})$/)
  if (hex3) {
    const [r, g, b] = hex3[1].split('')
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }

  const rgb = s.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (!rgb) {
    return '#78909c'
  }

  const h = (n) =>
    Math.max(0, Math.min(255, Number(n)))
      .toString(16)
      .padStart(2, '0')
  return `#${h(rgb[1])}${h(rgb[2])}${h(rgb[3])}`
}

export function carregarPrefsEscalas() {
  try {
    const bruto = localStorage.getItem(CHAVE)
    return bruto ? JSON.parse(bruto) : {}
  } catch {
    return {}
  }
}

function gravar(prefs) {
  const limpo = prefs && typeof prefs === 'object' ? { ...prefs } : {}
  if (!Array.isArray(limpo.cota) || limpo.cota.length !== 6) {
    delete limpo.cota
  }
  if (
    !limpo.diametroEscuro ||
    typeof limpo.diametroEscuro !== 'object' ||
    Array.isArray(limpo.diametroEscuro) ||
    Object.keys(limpo.diametroEscuro).length === 0
  ) {
    delete limpo.diametroEscuro
  }
  if (
    !limpo.diametroClaro ||
    typeof limpo.diametroClaro !== 'object' ||
    Array.isArray(limpo.diametroClaro) ||
    Object.keys(limpo.diametroClaro).length === 0
  ) {
    delete limpo.diametroClaro
  }
  delete limpo.diametroEditadoEscuro
  delete limpo.diametroEditadoClaro

  try {
    if (Object.keys(limpo).length === 0) {
      localStorage.removeItem(CHAVE)
      return
    }
    localStorage.setItem(CHAVE, JSON.stringify(limpo))
  } catch {
    // localStorage cheio/indisponível: o ajuste só não sobrevive ao reload.
  }
}

export function coresCotaSalvas() {
  const prefs = carregarPrefsEscalas()
  const cota = prefs.cota
  if (!Array.isArray(cota) || cota.length !== 6 || !cota.every(ehHex)) {
    return null
  }
  return cota.map((cor) => corParaHex(cor))
}

export function gravarCoresCota(cores) {
  const prefs = carregarPrefsEscalas()
  if (!Array.isArray(cores) || cores.length !== 6 || !cores.every(ehHex)) {
    return prefs
  }
  prefs.cota = cores.map((cor) => corParaHex(cor))
  gravar(prefs)
  return prefs
}

function limparMapaDiametro(mapa) {
  if (!mapa || typeof mapa !== 'object' || Array.isArray(mapa)) {
    return {}
  }

  const limpo = {}
  Object.keys(mapa).forEach((chave) => {
    if (ehHex(mapa[chave])) {
      limpo[chave] = corParaHex(mapa[chave])
    }
  })
  return limpo
}

export function coresDiametroSalvas(escuro) {
  const prefs = carregarPrefsEscalas()
  return limparMapaDiametro(escuro ? prefs.diametroEscuro : prefs.diametroClaro)
}

export function gravarCorDiametro(chaveValor, cor, escuro) {
  if (!ehHex(cor) || chaveValor == null || chaveValor === '') {
    return carregarPrefsEscalas()
  }

  const prefs = carregarPrefsEscalas()
  const campo = escuro ? 'diametroEscuro' : 'diametroClaro'
  prefs[campo] = limparMapaDiametro(prefs[campo])
  prefs[campo][String(chaveValor)] = corParaHex(cor)
  gravar(prefs)
  return prefs
}

export function gravarCoresDiametro(mapa, escuro) {
  const prefs = carregarPrefsEscalas()
  const campo = escuro ? 'diametroEscuro' : 'diametroClaro'
  prefs[campo] = limparMapaDiametro(mapa)
  gravar(prefs)
  return prefs
}

export function restaurarStopCota(indice, padrao) {
  if (
    !Array.isArray(padrao) ||
    padrao.length !== 6 ||
    indice < 0 ||
    indice >= 6
  ) {
    return carregarPrefsEscalas()
  }

  const atuais = coresCotaSalvas() || padrao.map((cor) => corParaHex(cor))
  const novas = atuais.slice()
  novas[indice] = corParaHex(padrao[indice])
  if (novas.every((cor, i) => cor === corParaHex(padrao[i]))) {
    return restaurarPadrao('cota')
  }
  return gravarCoresCota(novas)
}

export function restaurarPadrao(bloco) {
  const prefs = carregarPrefsEscalas()
  if (bloco === 'cota') {
    delete prefs.cota
  }
  gravar(prefs)
  return prefs
}
